import { apiFetch } from "../lib/api";
import { HierarchyAccessError, id, object, request, session, type HierarchyScope } from "./api";
import type { DocumentReference, TranscriptSelection } from "./transcript";
import { loadArtifactCatalog, type CatalogArtifact } from "./catalog";
import { boundedBytes } from "./response";

export interface DocumentSelection extends TranscriptSelection {
  nodeId: string;
  reference: DocumentReference;
}
export interface PinnedDocument {
  bytes: Uint8Array<ArrayBuffer>;
  contentType: string;
  name: string;
}
export interface CatalogDocumentSelection {
  catalog: CatalogArtifact;
  scope: HierarchyScope;
}
type VersionSelection = Pick<DocumentSelection, "accountId" | "workspaceId" | "reference">;

async function authorize(selection: DocumentSelection, expected?: HierarchyScope, signal?: AbortSignal): Promise<HierarchyScope> {
  const current = await session(signal);
  if (current.accountId !== selection.accountId || (expected && current.userId !== expected.userId)) throw new HierarchyAccessError();
  const root = `/code/workspaces/${encodeURIComponent(selection.workspaceId)}/threads/${encodeURIComponent(selection.threadId)}`;
  const thread = await request(root, signal);
  if (thread.id !== selection.threadId || thread.accountId !== selection.accountId || thread.workspaceId !== selection.workspaceId || (thread.projectId || undefined) !== selection.projectId) throw new HierarchyAccessError();
  const turn = await request(`${root}/turns/${encodeURIComponent(selection.nodeId)}`, signal);
  const scope = object(turn.scope);
  if (turn.id !== selection.nodeId || scope.run_id !== selection.nodeId || scope.account_id !== selection.accountId || scope.workspace_id !== selection.workspaceId) throw new HierarchyAccessError();
  const input = object(turn.input), result = object(turn.result);
  const refs = [input.artifacts ?? [], result.artifacts ?? []];
  if (!refs.every(Array.isArray) || !refs.flat().some((raw) => {
    const ref = object(raw);
    return ref.artifact_id === selection.reference.artifactId && ref.version_id === selection.reference.versionId;
  })) throw new HierarchyAccessError();
  return current;
}

function metadata(raw: unknown, selection: VersionSelection) {
  const value = object(raw);
  if (value.id !== selection.reference.versionId || value.artifactId !== selection.reference.artifactId ||
    value.accountId !== selection.accountId || value.workspaceId !== selection.workspaceId ||
    typeof value.sha256 !== "string" || !/^[a-f0-9]{64}$/.test(value.sha256) || typeof value.contentType !== "string") throw new HierarchyAccessError();
  return { sha256: value.sha256, contentType: value.contentType.split(";")[0].trim().toLowerCase(),
    name: typeof value.originalName === "string" && value.originalName ? value.originalName.replace(/[/\\\x00-\x1f\x7f]/g, "_") : "Saved document" };
}

export async function loadPinnedDocument(selection: DocumentSelection, signal?: AbortSignal): Promise<PinnedDocument> {
  for (const value of [selection.accountId, selection.workspaceId, selection.threadId, selection.nodeId, selection.reference.artifactId, selection.reference.versionId]) id(value);
  return loadAuthorizedVersion(selection, (expected) => authorize(selection, expected, signal), signal);
}

export async function loadCatalogDocument(selection: CatalogDocumentSelection, signal?: AbortSignal): Promise<PinnedDocument> {
  const artifact = selection.catalog;
  const version = { accountId: artifact.accountId, workspaceId: artifact.workspaceId,
    reference: { artifactId: id(artifact.id), versionId: id(artifact.headVersionId) } };
  return loadAuthorizedVersion(version, async () => {
    const current = await loadArtifactCatalog(artifact, signal, selection.scope);
    if (!current.artifacts.some((row) => row.id === artifact.id)) throw new HierarchyAccessError();
    // The selection pins the version visible at click time, not a moving head.
    return current.scope;
  }, signal);
}

async function loadAuthorizedVersion(selection: VersionSelection, check: (expected?: HierarchyScope) => Promise<HierarchyScope>, signal?: AbortSignal): Promise<PinnedDocument> {
  const identity = await check();
  const path = `/code/workspaces/${encodeURIComponent(selection.workspaceId)}/artifacts/${encodeURIComponent(selection.reference.artifactId)}/versions/${encodeURIComponent(selection.reference.versionId)}`;
  const response = await apiFetch(path, { credentials: "include", cache: "no-store", signal });
  let bytes: Uint8Array<ArrayBuffer>;
  let version: ReturnType<typeof metadata>;
  if (response.status === 413) {
    await response.body?.cancel();
    const ticket = await request(`${path}/download`, signal);
    version = metadata(ticket.version, selection);
    if (typeof ticket.url !== "string" || typeof ticket.expiresAt !== "string" || !(Date.parse(ticket.expiresAt) > Date.now())) throw new HierarchyAccessError();
    const url = new URL(ticket.url);
    if (url.protocol !== "https:" || url.username || url.password || url.hash) throw new HierarchyAccessError();
    // Signed storage URLs are capability-bearing. Never forward session cookies,
    // Authorization headers or referrers, and never follow a storage redirect.
    bytes = await boundedBytes(await fetch(url, { credentials: "omit", cache: "no-store", redirect: "error", referrerPolicy: "no-referrer", signal }), 100 << 20, signal);
  } else {
    const raw = await boundedBytes(response, (6 << 20), signal);
    const body = object(JSON.parse(new TextDecoder("utf-8", { fatal: true }).decode(raw)));
    version = metadata(body.version, selection);
    if (typeof body.contentBase64 !== "string") throw new HierarchyAccessError();
    const decoded = atob(body.contentBase64);
    if (decoded.length > 4 << 20 || btoa(decoded) !== body.contentBase64) throw new HierarchyAccessError();
    bytes = Uint8Array.from(decoded, (character) => character.charCodeAt(0));
  }
  const digest = Array.from(new Uint8Array(await crypto.subtle.digest("SHA-256", bytes)), (value) => value.toString(16).padStart(2, "0")).join("");
  if (digest !== version.sha256) throw new HierarchyAccessError();
  await check(identity);
  const current = await session(signal);
  if (current.accountId !== identity.accountId || current.userId !== identity.userId) throw new HierarchyAccessError();
  signal?.throwIfAborted();
  return { bytes, contentType: version.contentType, name: version.name };
}
